// Late Load Tracking Utility
// Automatically detects and records loads that are more than 2 hours late

import { supabase } from '../lib/supabase';
import { calculateTimeUntilDelivery } from './timezone';

interface LateLoadCheck {
  dispatchId: string;
  dispatchNumber: string;
  loadId: string;
  loadNumber: string;
  driverName: string;
  truckNumber: string;
  currentLat: number;
  currentLng: number;
  deliveryCity: string;
  deliveryState: string;
  scheduledDelivery: string;
  dispatchStatus: string;
}

/**
 * Check if a load should be recorded as late and record it if conditions are met
 */
export async function checkAndRecordLateLoad(check: LateLoadCheck): Promise<void> {
  try {
    console.log(`[Late Load Check] Checking ${check.dispatchNumber}...`);
    
    // Calculate how late the load is (in delivery location timezone)
    const timeUntilDelivery = calculateTimeUntilDelivery(
      check.scheduledDelivery,
      check.deliveryState
    );

    console.log(`[Late Load Check] ${check.dispatchNumber} - isOverdue: ${timeUntilDelivery.isOverdue}, totalMinutes: ${timeUntilDelivery.totalMinutes}`);

    // Only record if more than 2 hours late
    // Note: totalMinutes is negative when overdue (e.g., -300 means 300 minutes late)
    if (!timeUntilDelivery.isOverdue || Math.abs(timeUntilDelivery.totalMinutes) < 120) {
      console.log(`[Late Load Check] ${check.dispatchNumber} - Not late enough (needs 120+ minutes late, currently ${Math.abs(timeUntilDelivery.totalMinutes)} minutes)`);
      return;
    }

    // Check if this late load is already recorded
    const { data: existing } = await supabase
      .from('late_loads')
      .select('id, actual_delivery')
      .eq('dispatch_id', check.dispatchId)
      .single();

    const hoursLate = Math.round(Math.abs(timeUntilDelivery.totalMinutes) / 60);

    if (existing) {
      console.log(`[Late Load Check] ${check.dispatchNumber} - Already exists, updating...`);
      // Update existing record if not yet delivered
      if (!existing.actual_delivery && (check.dispatchStatus === 'delivered' || check.dispatchStatus === 'completed')) {
        const { error } = await supabase
          .from('late_loads')
          .update({
            actual_delivery: new Date().toISOString(),
            hours_late: hoursLate,
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id);
        
        if (error) {
          console.error(`[Late Load Check] Error updating ${check.dispatchNumber}:`, error);
        } else {
          console.log(`[Late Load Check] ${check.dispatchNumber} - Updated with actual delivery`);
        }
      } else if (!existing.actual_delivery) {
        // Update hours late while still in transit
        const { error } = await supabase
          .from('late_loads')
          .update({
            hours_late: hoursLate,
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id);
        
        if (error) {
          console.error(`[Late Load Check] Error updating ${check.dispatchNumber}:`, error);
        } else {
          console.log(`[Late Load Check] ${check.dispatchNumber} - Updated hours late to ${hoursLate}h`);
        }
      }
    } else {
      console.log(`[Late Load Check] ${check.dispatchNumber} - Creating new record (${hoursLate}h late)...`);
      // Create new late load record
      // Use scheduled delivery as placeholder for actual_delivery if not yet delivered
      const actualDelivery = (check.dispatchStatus === 'delivered' || check.dispatchStatus === 'completed') 
        ? new Date().toISOString() 
        : check.scheduledDelivery; // Placeholder until actual delivery

      const { error } = await supabase
        .from('late_loads')
        .insert({
          load_id: check.loadId,
          load_number: check.loadNumber,
          dispatch_id: check.dispatchId,
          dispatch_number: check.dispatchNumber,
          driver_name: check.driverName,
          scheduled_delivery: check.scheduledDelivery,
          actual_delivery: actualDelivery,
          hours_late: hoursLate,
          reason: 'Automatically detected - more than 2 hours late',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (error) {
        console.error(`[Late Load Check] Error inserting ${check.dispatchNumber}:`, error);
      } else {
        console.log(`[Late Load Check] ✅ Late load recorded: ${check.dispatchNumber} - ${hoursLate}h late`);
      }
    }
  } catch (error) {
    console.error('Error checking/recording late load:', error);
  }
}

/**
 * Batch check multiple loads for late status
 */
export async function batchCheckLateLoads(checks: LateLoadCheck[]): Promise<void> {
  // Process in parallel but with error handling for each
  await Promise.all(
    checks.map(check => checkAndRecordLateLoad(check).catch(err => {
      console.error(`Error processing late load check for ${check.dispatchNumber}:`, err);
    }))
  );
}
